import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, MoreHorizontal, Search, Users } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { SortableHeader } from '@/components/analytics/components/SortableHeader';
import { Loading, MatterMemberAvatars } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FIRM_MATTERS_PAGE_SIZE,
  type FirmMatterItem,
  type FirmMatterSortField,
  type FirmSortOrder,
  useFirmMatters,
} from '@/hooks/useFirmAdmin';
import useAllUsers from '@/hooks/useAllUsers';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { fullName } from '@/lib/user';
import { FilterSelect } from './FilterSelect';
import { FirmAdminEmptyState, FirmAdminShell } from './ShellTabs';
import { MatterMembersSheet } from './MatterMembersSheet';

export function MattersTab() {
  const searchInputId = useId();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedMemberGuids, setSelectedMemberGuids] = useState<string[]>([]);
  const [sortField, setSortField] = useState<FirmMatterSortField>('documents_count');
  const [sortOrder, setSortOrder] = useState<FirmSortOrder>('desc');
  const [page, setPage] = useState(1);
  const [membersMatter, setMembersMatter] = useState<FirmMatterItem | null>(null);
  const [matterToArchive, setMatterToArchive] = useState<FirmMatterItem | null>(null);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);

  const { users } = useAllUsers();
  const { data, isLoading, error, refresh } = useFirmMatters({
    q: debouncedQuery,
    memberGuids: selectedMemberGuids,
    sortField,
    sortOrder,
    page,
    pageSize: FIRM_MATTERS_PAGE_SIZE,
  });

  const matters = data?.matters ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FIRM_MATTERS_PAGE_SIZE));

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  const memberOptions = useMemo(() => {
    const nameCounts = new Map<string, number>();
    for (const user of users) {
      const name = fullName(user).trim() || user.email;
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    return users
      .map((user) => {
        const name = fullName(user).trim() || user.email;
        const label = (nameCounts.get(name) ?? 0) > 1 ? `${name} (${user.email})` : name;
        return {
          value: user.guid,
          label,
          searchValue: `${name} ${user.email} ${user.guid}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const handleSort = (field: FirmMatterSortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'name' || field === 'region' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const hasFilters = Boolean(query.trim()) || selectedMemberGuids.length > 0;

  const handleMembersChanged = async () => {
    try {
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err) || 'Failed to refresh matters');
    }
  };

  const setMatterActive = async (matter: FirmMatterItem, isActive: boolean) => {
    setIsTogglingArchive(true);
    try {
      await backendClient.patch(`/matter/update/${matter.matter_guid}`, {
        is_active: isActive,
      });
      toast.success(isActive ? 'Matter restored' : 'Matter archived');
      setMatterToArchive(null);
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err) || (isActive ? 'Failed to restore matter' : 'Failed to archive matter'));
    } finally {
      setIsTogglingArchive(false);
    }
  };

  return (
    <FirmAdminShell
      title="Matters"
      description="All matters in this firm."
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <label htmlFor={searchInputId} className="sr-only">Search matters</label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            id={searchInputId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by matter name or number"
            className="h-10 pl-9"
          />
        </div>
        <FilterSelect
          label="Member"
          allLabel="All members"
          selectedNoun="members"
          options={memberOptions}
          selected={selectedMemberGuids}
          onChange={(next) => {
            setSelectedMemberGuids(next);
            setPage(1);
          }}
        />
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <FirmAdminEmptyState
          icon={Users}
          title="Could not load matters"
          description="Try refreshing the page. If this keeps happening, contact support."
        />
      ) : (
        <div>
          <div className="overflow-hidden rounded-md border bg-white">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Matter"
                    field="name"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmMatterSortField)}
                    width="32%"
                  />
                  <SortableHeader
                    label="Region"
                    field="region"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmMatterSortField)}
                    width="14%"
                  />
                  <SortableHeader
                    label="Documents"
                    field="documents_count"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmMatterSortField)}
                    className="text-right"
                    width="100px"
                    tooltip="Documents uploaded in the past 30 days"
                  />
                  <SortableHeader
                    label="Recent chats"
                    field="messages_count"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmMatterSortField)}
                    className="text-right"
                    width="110px"
                    tooltip="Case partner messages in the past 30 days"
                  />
                  <TableHead className="text-right" style={{ width: '140px' }}>
                    Members
                  </TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.length > 0 ? (
                  matters.map((matter) => (
                    <TableRow key={matter.matter_guid}>
                      <TableCell>
                        <div>
                          <Link
                            to="/matter/$slug/overview"
                            params={{ slug: matter.matter_guid }}
                            className="font-medium text-slate-900 hover:text-crimson-700 hover:underline"
                          >
                            {matter.matter_name}
                          </Link>
                          {matter.matter_no ? (
                            <p className="text-xs text-muted-foreground">{matter.matter_no}</p>
                          ) : null}
                          {matter.case_reference && matter.case_reference !== matter.matter_no ? (
                            <p className="text-xs text-muted-foreground">{matter.case_reference}</p>
                          ) : null}
                          {!matter.is_active ? (
                            <p className="text-xs font-medium text-crimson">Archived</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{matter.region || '—'}</p>
                          {matter.forum ? (
                            <p className="text-xs text-muted-foreground">{matter.forum}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{matter.documents_count}</TableCell>
                      <TableCell className="text-right">{matter.messages_count}</TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <MatterMemberAvatars
                            members={matter.members || []}
                            totalCount={matter.users_count}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={MoreHorizontal}
                              aria-label={`Actions for ${matter.matter_name}`}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setMembersMatter(matter)}>
                              Matter Members
                            </DropdownMenuItem>
                            {matter.is_active ? (
                              <DropdownMenuItem onSelect={() => setMatterToArchive(matter)}>
                                Archive
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                disabled={isTogglingArchive}
                                onSelect={() => {
                                  void setMatterActive(matter, true);
                                }}
                              >
                                Restore
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <FirmAdminEmptyState
                        icon={Users}
                        title={hasFilters ? 'No matching matters' : 'No matters yet'}
                        description={
                          hasFilters
                            ? 'Try a different search or member filter.'
                            : 'When matters are created, they will appear here.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-2 flex items-center justify-between px-2">
            <span className="text-sm text-muted-foreground">
              {totalCount === 0
                ? '0 rows'
                : `Showing ${(page - 1) * FIRM_MATTERS_PAGE_SIZE + 1}–${Math.min(page * FIRM_MATTERS_PAGE_SIZE, totalCount)} of ${totalCount} rows`}
            </span>
            {totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  aria-label="Previous page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                />
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  aria-label="Next page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {membersMatter ? (
        <MatterMembersSheet
          matterGuid={membersMatter.matter_guid}
          matterName={membersMatter.matter_name}
          caseReference={membersMatter.case_reference}
          open={!!membersMatter}
          onOpenChange={(open) => {
            if (!open) setMembersMatter(null);
          }}
          onMembersChanged={handleMembersChanged}
        />
      ) : null}

      <AlertDialog
        open={!!matterToArchive}
        onOpenChange={(open) => {
          if (!open && !isTogglingArchive) setMatterToArchive(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this matter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive “{matterToArchive?.matter_name}”. Archived matters leave the
              active list on Home, but nothing is deleted. You can restore archived matters at any
              point.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingArchive}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!matterToArchive || isTogglingArchive}
              className="bg-crimson text-white hover:bg-crimson-500 focus:ring-crimson"
              onClick={(event) => {
                event.preventDefault();
                if (matterToArchive) void setMatterActive(matterToArchive, false);
              }}
            >
              {isTogglingArchive ? 'Archiving…' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FirmAdminShell>
  );
}
